<template>
  <div class="LKPublicationsFormdataList">
    <ComponentFilter
      ref="filters"
      @change="onChangeDebounced"
    />

    <BTableCommon
      type="publication-formdata"
      :list="list"
      :formdata-disabled="formdataDisabled"
      :pagination="pagination"
      :loading="isLoading"
      :actions="tableActions"
      fixed-header

      @action:history="onViewHistory"
      @action:view="onViewRevision"
      @action:edit="onEditStatus"
      @action:add="onAddForm"
      @action:delete="onDelete"
      @change:pagination="onChangePagination"
      @change:sort="onChangeSort"
    />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import {
  lkFormdataMapper as StoreMapper,
} from './store'
import {
  createLKPublicationsFormdataHistoryListModal, createLKPublicationsFormdataModal,
  createLKPublicationsFormdataRevisionsListModal,
} from './utils'
import {
  TABLE_ACTIONS,
  CURATOR_TABLE_ACTIONS,
} from '@/views/LKPublications/formdata/table-headers'

import BTableCommon from '@/components/tables/BTableCommon.vue'
import { rootMapper } from '@/store/modules/root'
import { DEFAULT_PER_PAGE } from '@/helpers/enums/params'
import { LKFormdataAdapter } from '@/views/LKPublications/formdata/store/LKFormdataAdapter'
import ComponentFilter from './components/LKPublicationsFormdataListFilter.vue'
import { FormdataStatuses } from '@/typings/api/Formdata'


const DEFAULT_PARAMS = {
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  count: 0,
}

export default {
  name: 'LKPublicationsFormdataList',
  components: {
    BTableCommon,
    ComponentFilter,
  },
  inheritAttrs: false,
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    form_template_id: {
      type: [String, Number],
      required: true,
    },
    formdataDisabled: Boolean,
  },
  data: (vm) => ({
    isLoading: false,
    defaultParams: {
      data_collection_id: vm.id,
    },
    list: [],
    pagination: { ...DEFAULT_PARAMS },
  }),
  computed: {
    ...rootMapper.mapGetters(['profile']),
    ...StoreMapper.mapState([
      'revisions',
      'formTemplate',
    ]),

    tableActions() {
      return this.profile.is_superuser ? TABLE_ACTIONS : CURATOR_TABLE_ACTIONS
    },
  },
  methods: {
    ...StoreMapper.mapActions([
      'revision',
      'getFormTemplate',
      'updateStatus',
      'getNotStarted',
      'createForm',
    ]),

    asyncData() {
      const params = { ...this.pagination, ...this.defaultParams }
      this.onGetData(params)
    },
    onChangeSort(v) {
      const value = v.replace('is_submitted', 'status')
      this.onChangePagination({ ordering: value })
    },
    onChangePagination(options) {
      const { filters, ate_filter } = this.prepareFilterData(this.$refs.filters.form)
      const params = {
        ...this.pagination,
        ...this.defaultParams,
        ...filters,
        ate_filter,
        ...options,
      }

      this.pagination.page = options.page || DEFAULT_PARAMS.page
      this.pagination.per_page = options.per_page || DEFAULT_PARAMS.per_page

      return this.onGetData(params)
    },
    async onGetData(params) {
      this.isLoading = true
      const adapter = new LKFormdataAdapter()

      try {
        const result = await adapter.api.getAll(params)
        this.list = result.data
        this.pagination.count = result.meta.count
      } catch (e) {
        this.$notice.errors(e)
      }
      this.isLoading = false
    },
    async onViewHistory(data) {
      if (data.id) {
        const propsData = { data }
        const modal = await createLKPublicationsFormdataHistoryListModal(this, propsData)
        modal.open()
      } else {
        this.$notice.create({ text: 'История недоступна. Форма не создавалась' })
      }
    },
    async onViewRevisions(data) {
      const propsData = { data, form_template_id: this.form_template_id }
      const modal = await createLKPublicationsFormdataRevisionsListModal(this, propsData)
      modal.open()
    },
    async onAddForm(data, callback) {
      const unwatch = callback?.()

      const { data_collection_id, user_id } = data

      try {
        const { id } = await this.createForm({ id: data_collection_id, userId: user_id })
        // eslint-disable-next-line no-param-reassign
        data.id = id
        // eslint-disable-next-line no-param-reassign
        data.status = FormdataStatuses.open
      } catch (error) {
        this.$notice.errors(error)
      }
      unwatch?.()
    },
    async onEditStatus(data, callback) {
      const unwatch = callback?.()
      let new_status
      switch (data.status) {
        case FormdataStatuses.prefilled:
          new_status = FormdataStatuses.again_opened
          break
        case FormdataStatuses.open:
          new_status = FormdataStatuses.again_opened
          break
        case FormdataStatuses.in_progress:
          new_status = FormdataStatuses.overdue
          break
        case FormdataStatuses.again_opened:
          if (data.is_submitted) {
            new_status = FormdataStatuses.close
          } else {
            new_status = FormdataStatuses.overdue
          }
          break
        case FormdataStatuses.submitted:
          new_status = FormdataStatuses.again_opened
          break
        case FormdataStatuses.overdue:
          new_status = FormdataStatuses.again_opened
          break
        case FormdataStatuses.close:
          new_status = FormdataStatuses.again_opened
          break
        default:
      }
      const params = { id: data.id, new_status }

      try {
        await this.updateStatus(params)
        // eslint-disable-next-line no-param-reassign
        data.status = new_status
      } catch (error) {
        this.$notice.errors(error)
      }

      unwatch?.()
    },
    async onDelete(data, callback) {
      const unwatch = callback?.()

      const adapter = new LKFormdataAdapter()
      try {
        await adapter.api.delete(data.id)

        await this.onChangePagination({})
      } catch (e) {
        this.$notice.errors(e)
      }

      unwatch?.()
    },
    async onViewRevision(data, callback) {
      if (this.profile.is_superuser) {
        await this.onViewRevisions(data)
        return
      }

      const { last_revision_id } = data
      const unwatch = callback?.()

      const options = {
        id: this.id,
        revision_id: last_revision_id,
      }

      const promises = [
        this.revision(options),
        this.formTemplate?.id === this.form_template_id
          ? this.formTemplate
          : this.getFormTemplate(this.form_template_id),
      ]

      try {
        const results = await Promise.all(promises)
        const propsData = { dataCollection: results[0], formTemplate: results[1], readonly: true }
        const modal = await createLKPublicationsFormdataModal(this, propsData)
        modal.open()
      } catch (error) {
        this.$notice.errors(error)
        return
      } finally {
        unwatch?.()
      }
    },
    async onChangeDebounced(options) {
      const { filters, ate_filter } = this.prepareFilterData(options)
      const params = {
        ...this.defaultParams,
        ...filters,
        ate_filter,
        pagination: {
          ...this.pagination,
          page: 1,
        },
      }

      await this.onGetData(params)
    },
    prepareFilterData(options) {
      const filters = cloneDeep(options)
      const ate_filter = filters.region?.id || filters.municipality?.id || filters.school?.id
      delete filters.region
      delete filters.municipality
      delete filters.school
      return { filters, ate_filter }
    },
  },
}
</script>

<style lang="scss" scoped>
.LKPublicationsFormdataList {
  height: 100%;
  overflow-y: auto;
}
</style>
